import { io } from "socket.io-client";
import { config } from "../config";

/**
 * Singleton Socket Manager for Real-Time Updates
 * Manages a single WebSocket connection across the entire application
 */
class SocketManager {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.eventHandlers = new Map(); // Map of event -> Set of handlers
    this.userId = null;
    this.username = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.heartbeatInterval = null;
  }

  /**
   * Initialize and connect the socket
   */
  connect(userId, username) {
    if (this.isConnected && this.userId === userId) {
      console.log('[SocketManager] Already connected');
      return;
    }

    // Disconnect existing connection if user changed
    if (this.socket && this.userId !== userId) {
      this.disconnect();
    }

    this.userId = userId;
    this.username = username;

    console.log('[SocketManager] Connecting...', { userId, username });

    this.socket = io(config.API_URI, {
      withCredentials: true,
      // Start with polling (works reliably through the CRA dev proxy), then
      // upgrade to websocket. Prevents the dev-only WS-upgrade handshake from
      // hanging/timing out. In production the upgrade to websocket still happens.
      transports: ['polling', 'websocket'],
      upgrade: true,
      timeout: 10000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.setupEventListeners();
    this.socket.emit('join', userId);
  }

  /**
   * Setup core socket event listeners
   */
  setupEventListeners() {
    this.socket.on('connect', () => {
      const wasReconnect = this.reconnectAttempts > 0;
      this.isConnected = true;
      this.reconnectAttempts = 0;

      // Re-join with userId on (re)connect
      if (this.userId) {
        this.socket.emit('join', this.userId);
      }

      // Start heartbeat
      this.startHeartbeat();

      // Notify subscribers so they can re-announce per-context state that the
      // server dropped on disconnect (e.g. page presence via join-page).
      this.dispatchEvent('socket-connected', { reconnected: wasReconnect });
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      this.stopHeartbeat();
      this.dispatchEvent('socket-disconnected', { reason });
    });

    this.socket.on('connect_error', (error) => {
      console.error('[SocketManager] Connection error:', error);
      this.reconnectAttempts++;
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('[SocketManager] Reconnected after', attemptNumber, 'attempts');
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[SocketManager] Reconnection failed after max attempts');
    });

    // Listen for user-action events and dispatch to registered handlers
    this.socket.on('user-action', (data) => {
      console.log('[SocketManager] Received user-action:', data.action, data);
      this.dispatchEvent('user-action', data);
    });

    // Listen for other common events
    this.socket.on('user-online-status', (data) => {
      this.dispatchEvent('user-online-status', data);
    });

    this.socket.on('newNotification', (data) => {
      this.dispatchEvent('newNotification', data);
    });

    this.socket.on('importProgress', (data) => {
      this.dispatchEvent('importProgress', data);
    });

    this.socket.on('importComplete', (data) => {
      this.dispatchEvent('importComplete', data);
    });

    this.socket.on('importError', (data) => {
      this.dispatchEvent('importError', data);
    });

    this.socket.on('online-users-list', (data) => {
      this.dispatchEvent('online-users-list', data);
    });

    this.socket.on('page-presence-update', (data) => {
      this.dispatchEvent('page-presence-update', data);
    });

    // Chat events
    this.socket.on('chat:message', (data) => {
      this.dispatchEvent('chat:message', data);
    });

    this.socket.on('chat:newMessage', (data) => {
      this.dispatchEvent('chat:newMessage', data);
    });

    this.socket.on('chat:typing', (data) => {
      this.dispatchEvent('chat:typing', data);
    });

    this.socket.on('chat:stopTyping', (data) => {
      this.dispatchEvent('chat:stopTyping', data);
    });

    this.socket.on('chat:seen', (data) => {
      this.dispatchEvent('chat:seen', data);
    });
  }

  /**
   * Start sending heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing interval
    
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.userId) {
        this.socket.emit('heartbeat', this.userId);
      }
    }, 5000); // Send heartbeat every 5 seconds
  }

  /**
   * Stop heartbeat interval
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Subscribe to a specific event
   * @param {string} event - Event name
   * @param {function} handler - Handler function
   * @returns {function} Unsubscribe function
   */
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    
    this.eventHandlers.get(event).add(handler);

    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from a specific event
   */
  off(event, handler) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).delete(handler);
      
      // Clean up empty sets
      if (this.eventHandlers.get(event).size === 0) {
        this.eventHandlers.delete(event);
      }
    }
  }

  /**
   * Dispatch event to all registered handlers
   */
  dispatchEvent(event, data) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[SocketManager] Error in handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Emit an event to the server
   */
  emit(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    } else {
      // Queue and retry when connected
      const retry = () => {
        if (this.socket && this.isConnected) {
          this.socket.emit(event, data);
        } else {
          setTimeout(retry, 500);
        }
      };
      setTimeout(retry, 500);
    }
  }

  /**
   * Disconnect the socket
   */
  disconnect() {
    console.log('[SocketManager] Disconnecting...');
    
    this.stopHeartbeat();
    
    if (this.socket) {
      if (this.userId) {
        this.socket.emit('leave', this.userId);
      }
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.isConnected = false;
    this.userId = null;
    this.username = null;
    this.eventHandlers.clear();
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      userId: this.userId,
      username: this.username,
      reconnectAttempts: this.reconnectAttempts,
    };
  }
}

// Export singleton instance
const socketManager = new SocketManager();
export default socketManager;

